import { createFileRoute, type SearchSchemaInput } from '@tanstack/react-router';
import { influencersQuery } from '@/services/influencers';
import { roleIncludes } from '@/features/team/role-gate';
import { InfluencersPage } from '@/features/influencers/influencers-page';
import { InfluencersFailed, InfluencersPending, OperatorInfluencersPage, type OperatorInfluencersSearch } from '@/features/influencers/operator/operator-influencers-page';
import { PersonaPage } from '@/features/operator/persona-page';
import { prefetchOperatorRoster } from '@/features/influencers/operator/roster-params';

const VIEWS = ['list', 'board', 'posts'] as const;
const STAGES = ['all', 'discovery', 'outreach', 'negotiations', 'contracting', 'content', 'live_posts', 'payments', 'not_interested'] as const;

export const Route = createFileRoute('/_authed/influencers')({
  // The list on All unless a link asks for a view, a stage (Home's stage rows) or a campaign. `found`: the operator's
  // "Found, not sent" tab (her page never reads it).
  validateSearch: (search: { view?: string; stage?: string; program?: string | number; found?: unknown } & SearchSchemaInput): OperatorInfluencersSearch => {
    const program = Number(search.program);
    return {
      view: (VIEWS as readonly string[]).includes(search.view ?? '') ? (search.view as OperatorInfluencersSearch['view']) : 'list',
      stage: (STAGES as readonly string[]).includes(search.stage ?? '') ? (search.stage as OperatorInfluencersSearch['stage']) : 'all',
      ...(Number.isInteger(program) && program > 0 ? { program } : {}),
      ...(search.found === true || search.found === 'true' ? { found: true as const } : {}),
    };
  },
  // Her role first (an API without roles answers 404: nothing narrows): a role without the page requests nothing and
  // sees the role card. Then the first slice of the list the URL asks for (started, not awaited: a tab change
  // never waits on the loader).
  loaderDeps: ({ search }) => search,
  loader: async ({ context: { queryClient, team, user }, deps, cause }) => {
    if (!team || !(await roleIncludes(queryClient, team, 'influencers'))) return;
    // A seat that may be an operator (not previewing): his params, once per change (`prefetchOperatorRoster`).
    if (prefetchOperatorRoster(queryClient, team, user?.id, deps, cause)) return;
    void queryClient.prefetchInfiniteQuery(
      influencersQuery(team.id, { view: deps.view, program: deps.program ?? null, stage: deps.view === 'list' ? deps.stage : 'all', q: '', platform: null, sort: 'needs_you', dir: 'desc' }),
    );
  },
  component: InfluencersRoute,
});

// The operator's page or hers: a client seat and Preview as get hers, exactly. While a seat that may be an operator
// waits for its answer: the app's loading shape, never a blank page; if that answer fails, a card with Try again (never
// her page, whose Review doors would record her verdict as his).
function InfluencersRoute() {
  return <PersonaPage operator={OperatorRoute} managed={ManagedRoute} pending={<InfluencersPending />} failed={<InfluencersFailed />} />;
}

function ManagedRoute() {
  return <InfluencersPage search={Route.useSearch()} />;
}

function OperatorRoute() {
  return <OperatorInfluencersPage search={Route.useSearch()} />;
}
